import { Checkbox } from '@mantine/core';
import * as React from 'react';
import { useMemo } from 'react';
import { MantineColumn } from '../../../../../../react-commons/components/table/MantineTable';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    defaultSystemUserRoleResource,
    SystemUserRoleResource,
    useSystemUserRoleActions,
} from '../../../../resource/resources/persistables/user/systemUserRole';
import { SystemUserRoleCreateAndEditDialog } from '../../../page-components/system-user-management/system-user-role/SystemUserRoleCreateAndEditDialog';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { CrudValidationTable } from '../../../reusable-components/tables/CrudValidationTable';

export const SystemUserRolesPageTab = () => {
    const nestedDialog = useNestedDialogFunction();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const systemUserRoleActions = useSystemUserRoleActions();
    const systemUserRoles = systemUserRoleActions.useGetAllResources();
    const deleteSystemUserRole = systemUserRoleActions.useDeleteResource();

    const openAddDialog = () => {
        nestedDialog.showNestedDialog(
            <SystemUserRoleCreateAndEditDialog
                label={'Add System User Role'}
                systemUserRole={defaultSystemUserRoleResource}
                onClose={nestedDialog.closeNestedDialog}
            />
        );
    };

    const openEditDialog = (systemUserRole: SystemUserRoleResource) => {
        nestedDialog.showNestedDialog(
            <SystemUserRoleCreateAndEditDialog
                label={'Edit System User Role'}
                systemUserRole={systemUserRole}
                onClose={nestedDialog.closeNestedDialog}
            />
        );
    };

    const deleteRole = (id: string, name: string) => {
        deleteResourceDialogFunction.deleteResource(id, name, ResourceType.SYSTEM_USER_ROLE, deleteSystemUserRole);
    };

    const sortedSystemUserRoles = useMemo(() => {
        if (!systemUserRoles.data) {
            return [];
        }
        return systemUserRoles.data.toSorted((a, b) => a.name.localeCompare(b.name));
    }, [systemUserRoles.data]);

    const columns = useMemo((): Array<MantineColumn<SystemUserRoleResource>> => {
        return [
            {
                type: 'string',
                header: 'Name',
                accessorKey: 'name',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.String
                        nullable={false}
                        readOnly={true}
                        value={row.original.name}
                        style={{ width: '100%' }}
                    />
                ),
            },
            {
                type: 'boolean',
                header: 'Server Admin',
                accessorKey: 'isServerAdmin',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isServerAdmin ?? false} />,
            },
            {
                type: 'boolean',
                header: 'Admin',
                accessorKey: 'isAdmin',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isAdmin ?? false} />,
            },
            {
                type: 'boolean',
                header: 'Manager of all projects',
                accessorKey: 'isManagerOfAllProjects',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isManagerOfAllProjects ?? false} />,
            },
            {
                type: 'boolean',
                header: 'Process Admin of all projects',
                accessorKey: 'isProcessAdminOfAllProjects',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isProcessAdminOfAllProjects ?? false} />,
            },
            {
                type: 'boolean',
                header: 'Reader of all projects',
                accessorKey: 'isReaderOfAllProjects',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isReaderOfAllProjects ?? false} />,
            },
        ];
    }, []);

    return (
        <div className={'main-window-inner-content'}>
            <CrudValidationTable
                columns={columns}
                data={sortedSystemUserRoles}
                label={'System User Roles'}
                onAdd={openAddDialog}
                onDelete={(resource) => {
                    deleteRole(resource.id, resource.name);
                }}
                onEdit={(resource) => {
                    const role = systemUserRoles.data?.find((r) => r.id === resource.id);
                    if (role) {
                        openEditDialog(role);
                    }
                }}
                initialPageSize={10}
            />
        </div>
    );
};
